import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { constantCreate, constantUpdate } from '@/shared/api/generated/constants/constants';
import type {
  ConstantBody,
  CreateConstantInputBodyType,
  FieldTypeBody,
} from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';
import { toast } from '@/shared/ui/Toast';

import { keyify } from '../SchemaPage/keyify';
import styles from './ConstantsPage.module.scss';

/** Виды значения константы — одно значение, без вложенности (как на сервере). */
const TYPES: CreateConstantInputBodyType[] = [
  'string',
  'text',
  'number',
  'boolean',
  'url',
  'image',
  'file',
];

/** «По умолчанию» в выпадающем списке редактора: пустое значение Select не принимает. */
const DEFAULT_WIDGET = '*';

/**
 * Описание константы — ключ, вид, группа, подсказка. Одна форма на создание
 * и на деталку.
 */
export function ConstantForm({
  constant,
  siteId,
  env,
  sections,
  fieldTypes,
  onSaved,
}: {
  constant?: ConstantBody | undefined;
  siteId: string;
  env: EnvKey;
  sections: string[];
  fieldTypes: FieldTypeBody[];
  onSaved: (constant: ConstantBody) => void;
}) {
  const editing = constant !== undefined;

  const [name, setName] = useState(constant?.name ?? '');
  const [key, setKey] = useState(constant?.key ?? '');
  const [keyTouched, setKeyTouched] = useState(editing);
  const [type, setType] = useState<CreateConstantInputBodyType>(constant?.type ?? 'string');
  const [widget, setWidget] = useState(constant?.widget ?? '');
  const [section, setSection] = useState(constant?.section ?? '');
  const [hint, setHint] = useState(constant?.hint ?? '');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const widgets = fieldTypes.find((item) => item.type === type)?.widgets ?? [];

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setBusy(true);

    const body = {
      name: name.trim(),
      key: key.trim(),
      type,
      widget,
      section: section.trim(),
      hint: hint.trim(),
    };
    const request = editing
      ? constantUpdate(constant.id, body, { 'If-Match': constant.etag })
      : constantCreate(siteId, kinds[env], body);

    request
      .then((response) => {
        toast.success(editing ? t.constants.saved : t.constants.created);
        onSaved(response.data as ConstantBody);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.detail : t.constants.failed);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['form']} onSubmit={handleSubmit} noValidate>
      <div className={styles['form__row']}>
        <Field label={t.constants.name} required error={error}>
          <Input
            autoFocus={!editing}
            value={name}
            placeholder={t.constants.namePlaceholder}
            maxLength={100}
            onChange={(event) => {
              setName(event.target.value);

              if (!keyTouched) {
                setKey(keyify(event.target.value));
              }
            }}
          />
        </Field>

        <Field
          label={t.constants.key}
          required
          hint={editing && key !== constant.key ? t.constants.keyChanged : t.constants.keyHint}
        >
          <Input
            mono
            value={key}
            maxLength={64}
            placeholder="phone"
            onChange={(event) => {
              setKeyTouched(true);
              setKey(event.target.value);
            }}
          />
        </Field>
      </div>

      <div className={styles['form__row']}>
        <Field label={t.constants.type}>
          <Select
            value={type}
            onValueChange={(value) => {
              setType(value as CreateConstantInputBodyType);
              setWidget('');
            }}
          >
            {TYPES.map((item) => (
              <SelectItem key={item} value={item}>
                {fieldTypes.find((other) => other.type === item)?.title ?? item}
              </SelectItem>
            ))}
          </Select>
        </Field>

        {widgets.length > 0 && (
          <Field label={t.constants.widget}>
            <Select
              value={widget === '' || widget === widgets[0]?.value ? DEFAULT_WIDGET : widget}
              onValueChange={(value) => {
                setWidget(value === DEFAULT_WIDGET ? '' : value);
              }}
            >
              {widgets.map((item, index) => (
                <SelectItem key={item.value} value={index === 0 ? DEFAULT_WIDGET : item.value}>
                  {item.title}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}
      </div>

      <Field label={t.constants.section} hint={t.constants.sectionHint}>
        <Input
          value={section}
          maxLength={60}
          list="constant-sections"
          onChange={(event) => {
            setSection(event.target.value);
          }}
        />
        <datalist id="constant-sections">
          {sections.map((item) => (
            <option key={item} value={item} />
          ))}
        </datalist>
      </Field>

      <Field label={t.constants.hint} hint={t.constants.hintHint}>
        <Input
          value={hint}
          maxLength={300}
          onChange={(event) => {
            setHint(event.target.value);
          }}
        />
      </Field>

      <div>
        <Button type="submit" loading={busy}>
          {editing ? t.constants.save : t.constants.create}
        </Button>
      </div>
    </form>
  );
}
